import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import { useIntl } from "react-intl";

import { ChildLayout, LayoutSchemaField } from "@mapmycustomers/shared/types/layout/FormLayout";
import { Alert, Button } from "@mapmycustomers/ui";

import { RootState } from "@app/store/rootReducer";

import {
  getEditingLayoutSchema,
  isEditingActivityEmailVariantLayout,
  isEditingActivityPrimaryLayout,
  isEditingChildLayout,
} from "../../store";
import { updateChildLayout, updateLayout } from "../../store/actions";
import isActivityRequiredRelationshipField from "../../util/isActivityRequiredRelationshipField";

import styles from "./ActivityRequiredRelationshipAlert.module.scss";
import messages from "./messages";

interface ActivityRequiredRelationshipAlertProps {
  childLayoutId?: ChildLayout["id"];
  className?: string;
  isActivityEmailVariantLayout: boolean;
  isActivityPrimaryLayout: boolean;
  isEditingChildLayout: boolean;
  layoutSchema: LayoutSchemaField[];
  onUpdateChildLayout: typeof updateChildLayout;
  onUpdateLayout: typeof updateLayout;
}

const ActivityRequiredRelationshipAlert: React.FC<ActivityRequiredRelationshipAlertProps> = ({
  className,
  isActivityEmailVariantLayout,
  isActivityPrimaryLayout,
  isEditingChildLayout,
  layoutSchema,
  onUpdateChildLayout,
  onUpdateLayout,
}) => {
  const intl = useIntl();

  const activityLayoutRequiredRelationshipFields = useMemo(() => {
    return isActivityPrimaryLayout || isActivityEmailVariantLayout
      ? layoutSchema.filter(isActivityRequiredRelationshipField)
      : [];
  }, [isActivityPrimaryLayout, isActivityEmailVariantLayout, layoutSchema]);

  const handleResetClick = useCallback(() => {
    const updatedSchema = layoutSchema.map((schemaField) => {
      const isRequiredRelationshipField = activityLayoutRequiredRelationshipFields.some(
        (requiredField) => requiredField.field === schemaField.field
      );

      return isRequiredRelationshipField
        ? {
            ...schemaField,
            required: false,
          }
        : schemaField;
    });

    if (isEditingChildLayout) {
      onUpdateChildLayout({
        schema: updatedSchema,
      });
    } else {
      onUpdateLayout({
        schema: updatedSchema,
      });
    }
  }, [
    activityLayoutRequiredRelationshipFields,
    layoutSchema,
    isEditingChildLayout,
    onUpdateChildLayout,
    onUpdateLayout,
  ]);

  if (
    (!isActivityPrimaryLayout && !isActivityEmailVariantLayout) ||
    !activityLayoutRequiredRelationshipFields.length
  ) {
    return null;
  }

  return (
    <Alert
      action={
        <Button className={styles.resetButton} onClick={handleResetClick} type="link">
          {intl.formatMessage(messages.resetButton)}
        </Button>
      }
      className={cn(styles.alert, className)}
      message={
        isActivityPrimaryLayout ? (
          <>
            <div className={styles.primaryLayoutMessage1}>
              {intl.formatMessage(messages.primaryLayoutMessage1)}
            </div>
            <div className={styles.message}>
              {intl.formatMessage(messages.primaryLayoutMessage2)}
            </div>
          </>
        ) : (
          <div className={styles.message}>
            {intl.formatMessage(messages.emailVariantLayoutMessage)}
          </div>
        )
      }
      showIcon
      type="warning"
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    isActivityEmailVariantLayout: isEditingActivityEmailVariantLayout(state),
    isActivityPrimaryLayout: isEditingActivityPrimaryLayout(state),
    isEditingChildLayout: isEditingChildLayout(state),
    layoutSchema: getEditingLayoutSchema(state),
  };
};

const mapDispatchToProps = {
  onUpdateChildLayout: updateChildLayout,
  onUpdateLayout: updateLayout,
};

export default connect(mapStateToProps, mapDispatchToProps)(ActivityRequiredRelationshipAlert);
